import { useQuery } from '@apollo/client';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import {
  Headline,
  SegmentedButton,
  type LabeledValue,
  ButtonGridView,
  Loader,
} from '@volumental/common';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SizeLocale } from '../../../api/gql/graphql';
import { LengthLabelQuery } from '../../../api/queries/survey-query';
import { useSessionStore } from '../../../store';
import { type LengthStep } from '../../../store/survey';
import { TryOtherShoe } from '../components/try-other-shoe';
import { findWithFallback } from './find-value';
import { selectTimeoutMs } from './constants';

const locales = [
  {
    label: 'EU 🇪🇺',
    value: SizeLocale.Eu,
  },
  {
    label: 'US 🇺🇸',
    value: SizeLocale.Us,
  },
  {
    label: 'UK 🇬🇧',
    value: SizeLocale.Uk,
  },
];

const getDefaultSizeLocale = (
  sizeLocale: SizeLocale | undefined,
): SizeLocale => {
  switch (sizeLocale) {
    case SizeLocale.SkateSize:
    case SizeLocale.Us:
    case SizeLocale.UsMen:
    case SizeLocale.UsWomen: {
      return SizeLocale.Us;
    }
    case SizeLocale.Uk: {
      return SizeLocale.Uk;
    }
    case SizeLocale.Eu:
    case SizeLocale.Au:
    case SizeLocale.AuMen:
    case SizeLocale.AuWomen:
    case SizeLocale.Mondopoint:
    case undefined: {
      return SizeLocale.Eu;
    }
  }
};

interface LengthSizeSelectorProps {
  survey: LengthStep;
  onClose: (lenght: LabeledValue<number>, locale: SizeLocale) => void;
}

export function LengthSizeSelector({
  survey,
  onClose,
}: LengthSizeSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const { sizeLocale: storedSizeLocale } = useSessionStore((store) => store);
  const [sizeLocale, setSizeLocale] = useState<SizeLocale>(
    survey.locale ?? getDefaultSizeLocale(storedSizeLocale),
  );

  const onReady = (selected: LabeledValue<number>): void => {
    if (survey.length !== selected || survey.locale !== sizeLocale) {
      usageTracker.track(UsageTrackerEvent.AddSizeToSurvey, {
        size: selected.value,
        locale: sizeLocale,
      });
    }
    onClose(selected, sizeLocale);
  };

  return (
    <>
      <Headline style={{ fontSize: '21px' }}>{t('shoeSize')}</Headline>
      <SegmentedButton
        groupAriaLabel={t('ariaLabels.locales')}
        options={locales}
        onSelect={(newLocale) => {
          setSizeLocale(newLocale as SizeLocale);
        }}
        selected={sizeLocale}
      />
      <SelectableSizes
        survey={survey}
        sizeLocale={sizeLocale}
        onReady={onReady}
      />
    </>
  );
}

interface SelectableSizesProps {
  survey: LengthStep;
  sizeLocale: SizeLocale;
  onReady: (selected: LabeledValue<number>) => void;
}
function SelectableSizes({
  survey: { brand, gender, length },
  sizeLocale,
  onReady,
}: SelectableSizesProps): JSX.Element {
  const { t } = useTranslation();
  const { data, loading } = useQuery(LengthLabelQuery, {
    variables: {
      genderAge: gender.value,
      brandName: brand.value,
      sizeLocale,
    },
  });
  const [optimisticValue, setOptimisticValue] = useState(length?.value);
  if (loading) {
    return <Loader />;
  }
  const lengthList =
    data?.recommendableLengthLabels?.map((item) => ({
      value: item.value,
      label: item.explanation,
    })) ?? [];

  const onSelect = (selected: number): void => {
    setOptimisticValue(selected);
    const selectedLength = findWithFallback(selected, lengthList);
    setTimeout(() => {
      onReady(selectedLength);
    }, selectTimeoutMs);
  };

  return (
    <>
      <ButtonGridView
        options={lengthList}
        onSelect={onSelect}
        selected={optimisticValue}
        groupAriaLabel={t('ariaLabels.lengthSize')}
      />
      {lengthList.length === 0 && <TryOtherShoe />}
    </>
  );
}
